import { GitBranchIcon, MailIcon, MessageSquareIcon } from "lucide-react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatRelativeTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import type { ThreadListItem } from "./types";

function parseThreadIcon(clericalThreadId: string) {
	if (clericalThreadId.startsWith("email:")) return MailIcon;
	return MessageSquareIcon;
}

function parseThreadTitle(clericalThreadId: string) {
	if (clericalThreadId.startsWith("email:")) {
		return "Email Thread";
	}
	if (clericalThreadId.startsWith("draft:")) {
		return "Draft Thread";
	}
	return "Thread";
}

export function ThreadCard({
	thread,
	to,
	selected,
}: {
	thread: ThreadListItem;
	to: string;
	selected: boolean;
}) {
	const Icon = parseThreadIcon(thread.clericalThreadId);
	const title = parseThreadTitle(thread.clericalThreadId);

	return (
		<Link to={to} className="block">
			<Card
				className={cn(
					"border transition-colors hover:bg-muted/30",
					selected && "ring-2 ring-sidebar-ring",
				)}
				size="sm"
			>
				<CardContent className="flex items-start gap-3">
					<div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
						<Icon className="size-4 text-muted-foreground" />
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex items-start justify-between gap-2">
							<div className="min-w-0">
								<p className="truncate font-medium">{title}</p>
								<p className="truncate font-mono text-xs text-muted-foreground">
									{thread.clericalThreadId}
								</p>
							</div>
							{thread.kind && (
								<Badge variant="secondary" className="shrink-0">
									{thread.kind}
								</Badge>
							)}
						</div>
						<div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
							<span>{formatRelativeTime(thread._creationTime)}</span>
							{thread.childCount > 0 && (
								<span className="inline-flex items-center gap-1">
									<GitBranchIcon className="size-3" />
									{thread.childCount}
								</span>
							)}
						</div>
						{thread.statusText && (
							<p className="mt-1 truncate text-xs text-muted-foreground">
								{thread.statusText}
							</p>
						)}
					</div>
				</CardContent>
			</Card>
		</Link>
	);
}
